import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowButton from '../../../../../components/arrow-button';
import { DialogConfirmation } from '../../../../../components/dialog/confirmation';
import { useBackdropContext } from '../../../../../context/backdrop';
import {
  ChannelTypeInternal,
  MeasurementChannelKind,
  MeterTypeInternal,
} from '../../../../../data-access/gql-types/graphql';
import { useRefetchData } from '../../../../../hooks/refresh-data/use-refetch-data';
import { toastError, toastSuccess } from '../../../../../utils/toast';
import { useResetAggregateMeasurements } from '../../../api/meter/meter.hooks';
import { useDevicesAndChannels } from '../../../hooks/devices-and-channels';
import { MeasurementChannelKindRequest } from '../../../types/graphql-rest-converter';

type MeasurementsConfigurationProps = {
  meterChannelId: string;
};

const MeasurementsConfiguration: React.FC<MeasurementsConfigurationProps> = ({ meterChannelId }) => {
  const { t } = useTranslation('configuration');
  const { t: tc } = useTranslation('common');
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { mutate } = useResetAggregateMeasurements();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { refetchChannelDetails } = useRefetchData();
  const { channel } = useDevicesAndChannels({ channelId: meterChannelId });

  const isStandalone =
    channel?.data?.type === ChannelTypeInternal.Meter && channel?.data?.meterType === MeterTypeInternal.Standalone;

  const handleReset = () => {
    if (meterChannelId) {
      turnOnBackdrop();

      mutate(
        { channelId: meterChannelId, kind: MeasurementChannelKindRequest[MeasurementChannelKind.Meter] },
        {
          onSuccess: async () => {
            turnOffBackdrop();

            setShowDialog(false);
            await refetchChannelDetails();
            toastSuccess({ content: t('resetMeasurements.success') });
          },
        },
      );
    } else {
      toastError({ content: tc('errors.somethingWentWrong') });
    }
  };

  return (
    <>
      <hr />
      <ArrowButton title={t('resetMeasurements.button')} onClick={() => setShowDialog(true)} className="m-t-24" small />
      <DialogConfirmation
        show={showDialog}
        setShow={setShowDialog}
        header={t('resetMeasurements.popupHeader')}
        content={t(`resetMeasurements.popupContent${isStandalone ? 'Long' : ''}`)}
        primaryBtnText={tc('buttons.continue')}
        secondaryBtnText={tc('buttons.cancel')}
        primaryBtnAction={handleReset}
        secondaryBtnAction={() => setShowDialog(false)}
        timer
      />
    </>
  );
};

export default MeasurementsConfiguration;
